// Live's layer editor (spec G13b-layer-editor): the composition as a pure model. Each layer keeps
// its frame as the document placed it (a Layerize layer's FAL box, the base the whole document)
// and its own transform: a move, a scale around the frame's centre, and whether it was removed
// (a removal only marks it, as live's, so an undo brings it back in place).
import type { DocumentLayer, LayerDocument, SourceRef } from "../../../api/generated/model";

export type { DocumentLayer, LayerDocument };

/** A rectangle in document pixels. */
export interface Frame {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where a layer came from: live's sources, shown in the rail. */
export type LayerSource = "original" | "generation" | "library" | "product" | "edit" | "text";

/** Live's source labels. */
export const SOURCE_LABEL: Record<LayerSource, string> = {
  original: "Original layer",
  generation: "My Generations",
  library: "Library",
  product: "Product",
  edit: "AI edit",
  text: "Text",
};

export interface PictureLayer {
  kind: "picture";
  id: string;
  name: string;
  frame: Frame;
  source: Exclude<LayerSource, "text">;
  /** What the picture is, as a job's request names a source: a job's output or an asset. "Edit
   * with AI" sends it unchanged (spec G13b, PR E2). */
  ref: SourceRef;
  url: string;
  /** The picture's own pixels, which its frame holds contain-fitted. */
  width: number;
  height: number;
}

export interface TextLayer {
  kind: "text";
  id: string;
  name: string;
  frame: Frame;
  source: "text";
  text: string;
  font: string;
  weight: "regular" | "bold";
  size: number;
  color: string;
  align: "left" | "center" | "right";
}

export type EditorLayer = PictureLayer | TextLayer;

export interface Transform {
  dx: number;
  dy: number;
  scale: number;
  removed: boolean;
}

/** The composition: `order` bottom to top, every layer by id, each one's transform. */
export interface Composition {
  width: number;
  height: number;
  order: string[];
  layers: Record<string, EditorLayer>;
  transforms: Record<string, Transform>;
}

/** Live's scale bounds and step (the inspector's 25 to 200 %, by 10). */
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 2;
export const SCALE_STEP = 0.1;

const NONE: Transform = { dx: 0, dy: 0, scale: 1, removed: false };

/** The composition a document opens as: its layers bottom to top, each in its box (the base the
 * whole document), named FAL's name or live's "Layer {n}" counted from the bottom, each the
 * Layerize's output. A layer with no picture to show is left out. */
export function compositionOf(document: LayerDocument, jobId: string): Composition {
  const shown = [...document.layers]
    .filter((layer) => layer.url !== null)
    .sort((a, b) => a.z_index - b.z_index);
  const layers: Record<string, EditorLayer> = {};
  const transforms: Record<string, Transform> = {};
  for (const [at, layer] of shown.entries()) {
    const id = `layer-${layer.z_index}`;
    const frame = layer.box ?? { x: 0, y: 0, width: document.width, height: document.height };
    layers[id] = {
      kind: "picture",
      id,
      name: layer.name?.trim() || `Layer ${at + 1}`,
      frame,
      source: "original",
      ref: { kind: "job_output", job_id: jobId, output_index: layer.output_index },
      url: layer.url!,
      // The picture's own size where the API recorded it, else its frame's.
      width: layer.width ?? frame.width,
      height: layer.height ?? frame.height,
    };
    transforms[id] = NONE;
  }
  return {
    width: document.width,
    height: document.height,
    order: shown.map((layer) => `layer-${layer.z_index}`),
    layers,
    transforms,
  };
}

/** A layer's place now: its frame moved, then scaled around the frame's centre. */
export function placement(composition: Composition, id: string): Frame {
  const layer = composition.layers[id]!;
  const { dx, dy, scale } = composition.transforms[id] ?? NONE;
  const { x, y, width, height } = layer.frame;
  const w = width * scale;
  const h = height * scale;
  return { x: x + dx + (width - w) / 2, y: y + dy + (height - h) / 2, width: w, height: h };
}

/** The layers shown, bottom to top. */
export function visible(composition: Composition): string[] {
  return composition.order.filter((id) => !(composition.transforms[id]?.removed ?? false));
}

function withTransform(
  composition: Composition,
  id: string,
  change: (current: Transform) => Transform,
): Composition {
  const current = composition.transforms[id];
  if (current === undefined) return composition;
  return { ...composition, transforms: { ...composition.transforms, [id]: change(current) } };
}

/** Moved by `dx`, `dy` document pixels (a drag, or live's arrow nudge). */
export function moved(composition: Composition, id: string, dx: number, dy: number): Composition {
  return withTransform(composition, id, (t) => ({ ...t, dx: t.dx + dx, dy: t.dy + dy }));
}

/** Scaled to `scale`, within live's 0.25 to 2. */
export function scaled(composition: Composition, id: string, scale: number): Composition {
  const bounded = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
  return withTransform(composition, id, (t) => ({ ...t, scale: bounded }));
}

/** Removed (live's delete): kept in place for an undo. */
export function removed(composition: Composition, id: string): Composition {
  return withTransform(composition, id, (t) => ({ ...t, removed: true }));
}

/**
 * A corner resize, as live's: the pointer's offset projected on the frame's diagonal, the
 * opposite corner fixed, the scale within 0.25 to 2. `corner` is the dragged one; `dx`, `dy`
 * the pointer's travel in document pixels since the gesture began, from `start`.
 */
export function resized(
  start: Composition,
  id: string,
  corner: "nw" | "ne" | "sw" | "se",
  dx: number,
  dy: number,
): Composition {
  const layer = start.layers[id]!;
  const before = placement(start, id);
  const sx = corner === "ne" || corner === "se" ? 1 : -1;
  const sy = corner === "sw" || corner === "se" ? 1 : -1;
  // The travel along the diagonal, as a share of the frame's diagonal.
  const diagonal = Math.hypot(before.width, before.height);
  const along = (dx * sx * before.width + dy * sy * before.height) / diagonal;
  const current = start.transforms[id]!.scale;
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, current * (1 + along / diagonal)));
  const width = layer.frame.width * scale;
  const height = layer.frame.height * scale;
  // The opposite corner stays where it was.
  const fixedX = sx === 1 ? before.x : before.x + before.width;
  const fixedY = sy === 1 ? before.y : before.y + before.height;
  const x = sx === 1 ? fixedX : fixedX - width;
  const y = sy === 1 ? fixedY : fixedY - height;
  // Back to a move of the frame: placement centres the scaled frame on the moved one.
  const dxNew = x - layer.frame.x - (layer.frame.width - width) / 2;
  const dyNew = y - layer.frame.y - (layer.frame.height - height) / 2;
  return withTransform(start, id, (t) => ({ ...t, scale, dx: dxNew, dy: dyNew }));
}

/** Live's "Center on canvas": the layer's place centred on the document, its scale kept. */
export function centered(composition: Composition, id: string): Composition {
  const layer = composition.layers[id];
  if (layer === undefined) return composition;
  const { frame } = layer;
  const dx = composition.width / 2 - (frame.x + frame.width / 2);
  const dy = composition.height / 2 - (frame.y + frame.height / 2);
  return withTransform(composition, id, (t) => ({ ...t, dx, dy }));
}

/**
 * Live's "Bring forward" (`step` 1) and "Send backward" (-1): the layer swaps places with the next
 * visible one above or below, removed layers skipped; at the top or the bottom it stays.
 */
export function restacked(composition: Composition, id: string, step: 1 | -1): Composition {
  const order = [...composition.order];
  const from = order.indexOf(id);
  if (from < 0) return composition;
  let to = from + step;
  while (to >= 0 && to < order.length && composition.transforms[order[to]!]?.removed) to += step;
  if (to < 0 || to >= order.length) return composition;
  order.splice(from, 1);
  order.splice(to, 0, id);
  return { ...composition, order };
}

/** Whether a visible layer sits above (`1`) or below (`-1`) this one: live's forward and back. */
export function canRestack(composition: Composition, id: string, step: 1 | -1): boolean {
  const shown = visible(composition);
  const at = shown.indexOf(id);
  return at >= 0 && at + step >= 0 && at + step < shown.length;
}

/** A width and a height in pixels. */
export interface Size {
  width: number;
  height: number;
}

/** Live's new picture's frame: centred on the document, shrunk to fit only when it is larger. */
export function containedFrame(document: Size, picture: Size): Frame {
  const shrink = Math.min(1, document.width / picture.width, document.height / picture.height);
  const width = picture.width * shrink;
  const height = picture.height * shrink;
  return { x: (document.width - width) / 2, y: (document.height - height) / 2, width, height };
}

/** A picture of `width` × `height` contain-fitted in `frame`, centred (live's contain). */
export function fittedIn(frame: Frame, width: number, height: number): Frame {
  const scale = Math.min(frame.width / width, frame.height / height);
  const w = width * scale;
  const h = height * scale;
  return { x: frame.x + (frame.width - w) / 2, y: frame.y + (frame.height - h) / 2, width: w, height: h };
}

/** A layer added on top (live's "Add layer"), unmoved and unscaled; an id already in it, never. */
export function added(composition: Composition, layer: EditorLayer): Composition {
  if (composition.layers[layer.id] !== undefined) return composition;
  return {
    ...composition,
    order: [...composition.order, layer.id],
    layers: { ...composition.layers, [layer.id]: layer },
    transforms: { ...composition.transforms, [layer.id]: NONE },
  };
}

/** A layer's content replaced (a text edit), its place, scale and order kept, as live's. */
export function updated(composition: Composition, layer: EditorLayer): Composition {
  const current = composition.layers[layer.id];
  if (current === undefined || current.kind !== layer.kind) return composition;
  return { ...composition, layers: { ...composition.layers, [layer.id]: layer } };
}
